import { api, ok } from "./api";

/** An image as the multipart form the API takes; the body object only satisfies the types. */
const imageForm = (file: File) => ({
  body: { file: file.name },
  bodySerializer: () => {
    const form = new FormData();
    form.set("file", file);
    return form;
  },
});

/**
 * My profile (P8-54) and the company's branding. Each call answers with the new /v1/me, so the
 * page puts it straight into the session's cache.
 */
export const accountActions = {
  rename: (name: string) => ok(api.PATCH("/v1/me", { body: { name } })),
  setPhoto: (file: File) => ok(api.PUT("/v1/me/avatar", imageForm(file))),
  removePhoto: () => ok(api.DELETE("/v1/me/avatar")),
  setLogo: (file: File) => ok(api.PUT("/v1/admin/company/logo", imageForm(file))),
  removeLogo: () => ok(api.DELETE("/v1/admin/company/logo")),
  setFullLogo: (file: File) => ok(api.PUT("/v1/admin/company/logo-full", imageForm(file))),
  removeFullLogo: () => ok(api.DELETE("/v1/admin/company/logo-full")),
};

/** A phone number as the API stores it: international format, e.g. +254712345678. */
export const phoneProblem = (phone: string) =>
  /^\+[1-9]\d{7,14}$/.test(phone.replace(/[\s-]/g, ""))
    ? undefined
    : "Enter the number in international format, starting with + and the country code.";

/** What staff read when changing their phone number fails, by better-auth's code. */
export function phoneCodeProblem(code: string): string {
  switch (code) {
    case "INVALID_OTP":
      return "That code isn't right. Check the SMS and try again.";
    case "OTP_EXPIRED":
      return "That code has expired. Send a new one.";
    case "TOO_MANY_ATTEMPTS":
      return "Too many wrong codes. Send a new one.";
    case "PHONE_NUMBER_EXIST":
      return "Someone else already uses this number.";
    case "SMS_NOT_CONFIGURED":
      return "SMS isn't set up for this company yet, so the number can't be confirmed. Ask an administrator.";
    case "SMS_SEND_FAILED":
      return "We couldn't send the SMS. Try again in a few minutes.";
    case "TOO_MANY_REQUESTS":
    case "HTTP_429":
      return "Too many codes sent. Wait a minute and try again.";
    case "NETWORK_ERROR":
      return "The console can't reach MajiTap. Check your connection and try again.";
    default:
      return "Something went wrong on our side. Try again in a moment.";
  }
}

/** What staff read when an image is refused. */
export function imageProblem(code: string | undefined): string | undefined {
  switch (code) {
    case "FILE_TOO_LARGE":
      return "That image is over 2 MB. Choose a smaller one.";
    case "FILE_TYPE_NOT_ALLOWED":
      return "Choose a JPEG, PNG or WebP image.";
    case "FILE_EMPTY":
      return "That file is empty.";
    default:
      return undefined;
  }
}
